import { Injectable, Injector } from '@angular/core';
import { SortOption } from '@core';
import { SettingsService } from '@delon/theme';
import { Observable } from 'rxjs';
import { DocListBaseService } from 'src/app/shared-feature/services/_base/doc-list-base.service';
import { IFilter } from 'src/app/shared/types/filters';
import currencies from 'src/assets/data/currencies.json';
import countries from 'src/assets/data/einvoice-origin-country.json';
@Injectable()
export class SinvListService extends DocListBaseService {
  url = 'api/salesinvoices/list';
  expandProperties = '';
  override searchColumns: string[] = [
    'DocCode',
    'Description',
    'CustomerCode',
    'CustomerName',
    'AgentCode',
    'TermCode'
  ];
  sortOptions: SortOption[] = [
    { title: 'Date', sort: 'docDate' },
    { title: 'Doc #', sort: 'docCode' },
    { title: 'Customer #', sort: 'CustomerCode' },
    { title: 'Customer Name', sort: 'CustomerName' },
    { title: 'Agent #', sort: 'AgentCode' },
    { title: 'Term', sort: 'TermCode' },
    { title: 'Due Date', sort: 'dueDate' },
    { title: 'Amount', sort: 'netTotalAmount' },
    { title: 'Ref No.', sort: 'referenceNo' },
    { title: 'Our DO #', sort: 'ourDONumber' },
    { title: 'Validated On', sort: 'validateOn' }
  ];
  override filterColumns: IFilter[] = [
    { key: 'docDate', type: 'date-range', label: 'Doc Date' },
    {
      key: 'CustomerCode',
      type: 'select',
      label: 'Customer',
      url: 'api/customers/search?$orderby=name%20asc',
      keyField: 'code',
      valueField: 'name'
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { key: 'All', label: 'All' },
        {
          key: 'FullyPaid',
          label: 'Fully Paid',
          onSelect: key => 'OutStandingAmount eq 0'
        },
        {
          key: 'PartiallyPaid',
          label: 'Partially Paid',
          onSelect: key =>
            'OutstandingAmount gt 0 and OutstandingAmount lt NetTotalAmount'
        },
        {
          key: this.parkAndPost ? 'Approved' : 'Unpaid',
          label: this.parkAndPost ? 'Approved' : 'Unpaid',
          onSelect: key => this.parkAndPost ? 'DocumentStatus eq 2 and OutstandingAmount eq NetTotalAmount' : 'OutstandingAmount eq NetTotalAmount'
        },
        {
          key: 'Cancelled',
          label: 'Cancelled',
          onSelect: key => 'isCancelled eq true'
        }
      ]
    },
    {
      key: 'eInvStatus',
      type: 'select',
      label: 'E-Invoice Status',
      options: [
        { key: 'All', label: 'All' },
        {
          key: 'Submitted',
          label: 'Submitted',
          onSelect: key =>
            `(EInvoiceStatus eq '${key}' or ConsolidatedStatus eq '${key}')`
        },
        {
          key: 'Valid',
          label: 'Valid',
          onSelect: key =>
            `(EInvoiceStatus eq '${key}' or ConsolidatedStatus eq '${key}')`
        },
        {
          key: 'Invalid',
          label: 'Invalid',
          onSelect: key =>
            `(EInvoiceStatus eq '${key}' or ConsolidatedStatus eq '${key}')`
        },
        {
          key: 'Cancelled',
          label: 'Cancelled',
          onSelect: key =>
            `(EInvoiceStatus eq '${key}' or ConsolidatedStatus eq '${key}')`
        },
        {
          key: 'Pending',
          label: 'Pending',
          onSelect: key =>
            `(EInvoiceStatus ne 'Submitted' and ConsolidatedStatus ne 'Submitted' and EInvoiceStatus ne 'Valid' and ConsolidatedStatus ne 'Valid' and EInvoiceStatus ne 'Invalid' and ConsolidatedStatus ne 'Invalid' and EInvoiceStatus ne 'Cancelled' and ConsolidatedStatus ne 'Cancelled')`
        }
      ]
    },
    {
      key: 'validationControl',
      type: 'select',
      label: 'Validation Control',
      options: [
        { key: 'All', label: 'All' },
        { key: 'NA', label: 'NA', onSelect: key => 'EInvoiceControl eq -1' },
        {
          key: ' Draft',
          label: 'Draft',
          onSelect: key => 'EInvoiceControl eq 0'
        },
        {
          key: 'Portal',
          label: 'Portal',
          onSelect: key => 'EInvoiceControl eq 1'
        },
        {
          key: 'FullValidation',
          label: 'Full Validation',
          onSelect: key => 'EInvoiceControl eq 2'
        },
        {
          key: 'Exempted',
          label: 'Exempted',
          onSelect: key => 'EInvoiceControl eq 3'
        }
      ]
    },
    {
      key: 'Country',
      type: 'select',
      label: 'Country',
      options: [{ code: 'All', name: 'All' }, ...countries].map(c => ({
        key: c.code,
        label: c.name,
        onSelect: key => (key === 'All' ? '' : `Country eq '${key}'`)
      }))
    },

    { key: 'ValidateOn', type: 'date-range', label: 'Validated On' },
    { key: 'State', type: 'text', label: 'State' },
    { key: 'Remark1', type: 'text', label: 'Remark 1' },
    { key: 'CancelledDate', type: 'date-range', label: 'Canceled On' },
    { key: 'City', type: 'text', label: 'City' },
    { key: 'Remark2', type: 'text', label: 'Remark 2' },
    { key: 'CancelledRemark', type: 'text', label: 'Canceled Reason' },
    { key: 'Remark3', type: 'text', label: 'Remark 3' },
    {
      key: 'StockLocationCode',
      type: 'select',
      label: 'Location',
      url: 'api/stocklocations/lookup?$top=20&$skip=0&$orderby=code%20asc&$filter=isActive%20eq%20true',
      keyField: 'code',
      valueField: 'code'
    },
    { key: 'Ref1', type: 'text', label: 'Reference 1' },
    { key: 'Remark4', type: 'text', label: 'Remark 4' },
    {
      key: 'AgentCode',
      type: 'select',
      label: 'Doc Agent',
      url: 'api/agents/lookup?$top=20&$skip=0&$orderby=code%20asc&$filter=isActive%20eq%20true',
      keyField: 'code',
      valueField: 'code'
    },
    { key: 'Ref2', type: 'text', label: 'Reference 2' },
    { key: 'Notes', type: 'text', label: 'Notes' },
    {
      key: 'ProjectCode',
      type: 'select',
      label: 'Project',
      url: 'api/projects/lookup?$top=20&$skip=0&$orderby=code%20asc&$filter=(isActive%20eq%20true)%20and%20hasChildren%20eq%20false',
      keyField: 'code',
      valueField: 'code'
    },
    { key: 'Ref3', type: 'text', label: 'Reference 3' },
    {
      key: 'IsTaxInclusive',
      type: 'select',
      label: 'Tax Inclusive',
      options: [
        { key: 'All', label: 'All' },
        { key: 'Yes', label: 'Yes', onSelect: key => 'IsTaxInclusive eq true' },
        { key: 'No', label: 'No', onSelect: key => 'IsTaxInclusive eq false' }
      ]
    },
    {
      key: 'CurrencyCode',
      type: 'select',
      label: 'Currency',
      options: [{ code: 'All', name: 'All' }, ...currencies].map(x => ({
        key: x.code,
        label: x.name,
        onSelect: key => (key === 'All' ? '' : `CurrencyCode eq '${key}'`)
      }))
    },
    { key: 'Ref4', type: 'text', label: 'Reference 4' },
    { key: 'referenceNo', type: 'text', label: 'Ref No' },
    { key: 'ZipCode', type: 'text', label: 'ZipCode' }
  ];
  override onAction(action: string, data: any): Promise<boolean> {
    if (action === 'matchToAROR') {
      return this.confirmValidToKnockoff(data.map((x: any) => x.id));
    }
    if (action === 'bulkApprove') {
      return this.confirmApprove(data.map((x: any) => x.id));
    }
    return super.onAction(action, data);
  }

  batchDelete(ids: any[]): Observable<any> {
    return this.http.delete('api/salesinvoices', null, {
      body: { ids: ids }
    });
  }
  constructor(injector: Injector) {
    super(injector);
    const settings = injector.get(SettingsService);
    const country = settings.user['company']?.['country'];
    this.filterColumns = this.filterColumns.filter(e => {
      if (country === 'PH') {
        if (
          e.key === 'Country' ||
          e.key === 'State' ||
          e.key === 'City' ||
          e.key === 'CancelledRemark' ||
          e.key === 'validationControl' ||
          e.key === 'eInvStatus' ||
          e.key === 'ValidateOn' ||
          e.key === 'CancelledDate'
        ) {
          return false;
        }
      }
      return true;
    });
  }

  confirmValidToKnockoff(ids: any[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .post(
          'api/salesInvoices/IsValidKnockedoff',
          { ids: ids },
          { _silent: true }
        )
        .subscribe(res => {
          if (res > 0) {
            this.match(ids, res);
            resolve(true);
          } else {
            if (res === -1) {
              this.msg.error('<b>Sales Invoice(s) is already knocked-off.');
            }
            if (res === -2) {
              this.msg.error(
                '<b>Sales Invoice(s) that have been selected have different customer IDs.'
              );
            }
            reject();
          }
        });
    });
  }

  match(row: any[], customerId: any) {
    this.router.navigate(['/ar/or/detail/0'], {
      queryParams: {
        customerId: customerId,
        docType: 'SINV',
        toKnockoffBatch: row
      }
    });
  }

  confirmApprove(ids: any[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post('api/salesinvoices/ApproveBulk', { ids: ids })
      .subscribe({
        next: (res) => {
          if(res > 0) {
            resolve(true);
          }
          else {
            reject();
          }
        },
        error: (error) => {
          reject();
        }
      });
    });
  }
}
